import { useEffect, useState, useCallback, useRef } from 'react';
import { useCustom, useCustomMutation, useInvalidate } from '@refinedev/core';
import type { useEditor } from '@tiptap/react';
import { message } from 'antd';
import type { BlogArticle } from './BlogArticleTable';
import type { SeoSuggestions } from './SeoSuggestionsPanel';
import type { GenerationStep } from './BlogGenerationPipeline';

const generateSlug = (title: string) =>
  title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export function useBlogEditorLogic(
  open: boolean,
  initialArticle: BlogArticle | null,
  editor: ReturnType<typeof useEditor>,
  onClose: () => void,
) {
  const invalidate = useInvalidate();
  const { mutate: apiCall, isLoading: saving } = useCustomMutation();
  const { mutate: generateContent } = useCustomMutation();
  const { mutate: fetchSeo } = useCustomMutation();
  const autoSaveRef = useRef(false);

  const [liveArticleId, setLiveArticleId] = useState<string | null>(null);
  const effectiveId = liveArticleId ?? initialArticle?.id ?? null;
  const isEdit = effectiveId !== null;

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [seoKeywords, setSeoKeywords] = useState<string[]>([]);
  const [persona, setPersona] = useState<string>('developer');
  const [customPersona, setCustomPersona] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [featuredImageUrl, setFeaturedImageUrl] = useState('');
  const [locale, setLocale] = useState<string>('fr');
  const [autoTranslate, setAutoTranslate] = useState(true);
  const [generationStep, setGenerationStep] = useState<GenerationStep>('idle');
  const [loadingSeo, setLoadingSeo] = useState(false);
  const [suggestions, setSuggestions] = useState<SeoSuggestions | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);

  const isGenerating = !['idle', 'done'].includes(generationStep);
  const otherLocaleLabel = locale === 'fr' ? '🇬🇧 EN' : '🇫🇷 FR';

  const { refetch: fetchArticleDetail } = useCustom<BlogArticle>({
    url: `/api/v1/admin/marketing/blog/${initialArticle?.id ?? 'none'}`,
    method: 'get',
    queryOptions: { enabled: false },
  });

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setLiveArticleId(null);
    if (initialArticle) {
      setTitle(initialArticle.title);
      setSlug(initialArticle.slug);
      setLocale(initialArticle.locale ?? 'fr');
      setFeaturedImageUrl('');
      setDetailLoading(true);
      fetchArticleDetail()
        .then((res) => {
          const d = res?.data?.data;
          if (d) {
            setSeoTitle(d.seoTitle ?? '');
            setSeoDescription(d.seoDescription ?? '');
            setSeoKeywords(d.seoKeywords ?? []);
            setExcerpt(d.excerpt ?? '');
            setFeaturedImageUrl(((d as unknown as Record<string, unknown>).featuredImageUrl as string) ?? '');
            editor?.commands.setContent(d.content ?? '');
          } else {
            setSeoTitle(initialArticle.seoTitle ?? '');
            setSeoDescription(initialArticle.seoDescription ?? '');
            setSeoKeywords(initialArticle.seoKeywords ?? []);
            setExcerpt(initialArticle.excerpt ?? '');
            setFeaturedImageUrl((initialArticle as unknown as Record<string, string>).featuredImageUrl ?? '');
            editor?.commands.setContent(initialArticle.content ?? '');
          }
          setDetailLoading(false);
        })
        .catch(() => {
          setSeoTitle(initialArticle.seoTitle ?? '');
          setSeoDescription(initialArticle.seoDescription ?? '');
          setSeoKeywords(initialArticle.seoKeywords ?? []);
          setExcerpt(initialArticle.excerpt ?? '');
          setFeaturedImageUrl((initialArticle as unknown as Record<string, string>).featuredImageUrl ?? '');
          editor?.commands.setContent(initialArticle.content ?? '');
          setDetailLoading(false);
        });
    } else {
      setTitle('');
      setSlug('');
      setSeoTitle('');
      setSeoDescription('');
      setSeoKeywords([]);
      setPersona('developer');
      setCustomPersona('');
      setExcerpt('');
      setFeaturedImageUrl('');
      setLocale('fr');
      setAutoTranslate(true);
      editor?.commands.setContent('');
    }
    setSuggestions(null);
    setGenerationStep('idle');
  }, [open, initialArticle, editor, fetchArticleDetail]);

  useEffect(() => {
    if (!initialArticle && !liveArticleId) setSlug(generateSlug(title));
  }, [title, initialArticle, liveArticleId]);

  const refreshData = () => invalidate({ invalidates: ['all'] });
  const effectivePersona = persona === 'custom' ? customPersona : persona;

  const handleSave = useCallback(
    (publish: boolean) => {
      const content = editor?.getHTML() ?? '';
      const payload = {
        title,
        content,
        author: 'Growth Team',
        excerpt: excerpt || undefined,
        seoTitle: seoTitle || undefined,
        seoDescription: seoDescription || undefined,
        seoKeywords,
        locale,
        featuredImageUrl: featuredImageUrl || undefined,
      };
      if (isEdit && effectiveId) {
        apiCall(
          { url: `/api/v1/admin/marketing/blog/${effectiveId}`, method: 'patch', values: payload },
          {
            onSuccess: () => {
              if (publish) {
                apiCall(
                  { url: `/api/v1/admin/marketing/blog/${effectiveId}/publish`, method: 'post', values: {} },
                  {
                    onSuccess: () => {
                      message.success('Article publié (+ version traduite si existante)');
                      refreshData();
                      onClose();
                    },
                    onError: (e) => message.error(e?.message || 'Erreur publication'),
                  },
                );
              } else {
                message.success('Article sauvegardé');
                refreshData();
                onClose();
              }
            },
            onError: () => message.error('Erreur sauvegarde'),
          },
        );
      } else {
        apiCall(
          { url: '/api/v1/admin/marketing/blog', method: 'post', values: { ...payload, autoTranslate } },
          {
            onSuccess: () => {
              message.success('Article créé');
              refreshData();
              onClose();
            },
            onError: () => message.error('Erreur création'),
          },
        );
      }
    },
    [title, excerpt, seoTitle, seoDescription, seoKeywords, locale, autoTranslate, featuredImageUrl, editor, isEdit, effectiveId, apiCall, onClose, refreshData],
  );

  const doAutoSave = useCallback(
    (contentHtml: string, seo: { title: string; desc: string; kw: string[]; excerpt: string }, targetId: string) => {
      apiCall(
        {
          url: `/api/v1/admin/marketing/blog/${targetId}`,
          method: 'patch',
          values: {
            title,
            content: contentHtml,
            author: 'Growth Team',
            excerpt: seo.excerpt || undefined,
            seoTitle: seo.title || undefined,
            seoDescription: seo.desc || undefined,
            seoKeywords: seo.kw,
            locale,
          },
        },
        {
          onSuccess: () => {
            refreshData();
            if (autoTranslate) {
              setGenerationStep('translating');
              apiCall(
                { url: `/api/v1/admin/marketing/blog/${targetId}/translate`, method: 'post', values: {} },
                {
                  onSuccess: () => {
                    refreshData();
                    setGenerationStep('done');
                    setTimeout(() => setGenerationStep('idle'), 3000);
                  },
                  onError: (err) => {
                    if ((err?.message || '').includes('already exists')) {
                      apiCall(
                        { url: `/api/v1/admin/marketing/blog/${targetId}/sync-translation`, method: 'post', values: {} },
                        {
                          onSuccess: () => {
                            refreshData();
                            setGenerationStep('done');
                            setTimeout(() => setGenerationStep('idle'), 3000);
                          },
                          onError: () => {
                            setGenerationStep('done');
                            setTimeout(() => setGenerationStep('idle'), 3000);
                          },
                        },
                      );
                    } else {
                      setGenerationStep('done');
                      setTimeout(() => setGenerationStep('idle'), 3000);
                    }
                  },
                },
              );
            } else {
              setGenerationStep('done');
              setTimeout(() => setGenerationStep('idle'), 3000);
            }
          },
          onError: () => {
            message.warning('Contenu généré mais sauvegarde auto échouée.');
            setGenerationStep('idle');
          },
        },
      );
    },
    [title, locale, autoTranslate, apiCall, refreshData],
  );

  const launchGeneration = useCallback(
    (articleId: string) => {
      setGenerationStep('writing');
      generateContent(
        {
          url: `/api/v1/admin/marketing/blog/${articleId}/generate`,
          method: 'post',
          values: { topic: title, persona: effectivePersona },
        },
        {
          onSuccess: (res) => {
            const d = res?.data ?? {};
            const html = d.content ?? d.data?.content ?? '';
            if (html) editor?.commands.setContent(html);
            setGenerationStep('seo');
            const nSeo = d.seoTitle ?? '';
            const nDesc = d.seoDescription ?? '';
            const nKw = d.seoKeywords ?? [];
            const nExc = d.excerpt ?? '';
            if (nSeo) setSeoTitle(nSeo);
            if (nDesc) setSeoDescription(nDesc);
            if (nKw.length) setSeoKeywords(nKw);
            if (nExc) setExcerpt(nExc);
            setTimeout(() => {
              setGenerationStep('saving');
              doAutoSave(html, { title: nSeo, desc: nDesc, kw: nKw, excerpt: nExc }, articleId);
            }, 600);
          },
          onError: (err) => {
            message.error(err?.message || 'Erreur génération IA');
            setGenerationStep('idle');
          },
        },
      );
    },
    [title, effectivePersona, editor, generateContent, doAutoSave],
  );

  const handleGenerateAI = useCallback(() => {
    if (!title.trim()) {
      message.warning('Renseignez un titre');
      return;
    }
    setGenerationStep('context');
    autoSaveRef.current = true;
    if (effectiveId) {
      setTimeout(() => launchGeneration(effectiveId), 1500);
    } else {
      apiCall(
        {
          url: '/api/v1/admin/marketing/blog',
          method: 'post',
          values: { title, content: '<p></p>', author: 'Growth Team', locale, autoTranslate: false },
        },
        {
          onSuccess: (res) => {
            const newId = String(res?.data?.id ?? '');
            if (!newId) {
              message.error('Erreur création brouillon');
              setGenerationStep('idle');
              return;
            }
            setLiveArticleId(newId);
            refreshData();
            setTimeout(() => launchGeneration(newId), 1000);
          },
          onError: () => {
            message.error('Erreur création brouillon');
            setGenerationStep('idle');
          },
        },
      );
    }
  }, [title, effectiveId, locale, apiCall, launchGeneration, refreshData]);

  const handleTranslateOrSync = useCallback(
    (mode: 'translate' | 'sync' = 'sync') => {
      if (!effectiveId) {
        message.warning("Sauvegardez d'abord l'article");
        return;
      }
      setTranslating(true);
      const endpoint =
        mode === 'sync'
          ? `/api/v1/admin/marketing/blog/${effectiveId}/sync-translation`
          : `/api/v1/admin/marketing/blog/${effectiveId}/translate`;
      apiCall(
        { url: endpoint, method: 'post', values: {} },
        {
          onSuccess: () => {
            message.success(`Version ${otherLocaleLabel} ${mode === 'sync' ? 'synchronisée' : 'créée'} ✨`);
            refreshData();
            setTranslating(false);
          },
          onError: (err) => {
            const msg = err?.message || '';
            if (msg.includes('already exists') && mode === 'translate') {
              handleTranslateOrSync('sync');
            } else if (msg.includes('No translated version') && mode === 'sync') {
              handleTranslateOrSync('translate');
            } else {
              message.error(`Erreur : ${msg}`);
              setTranslating(false);
            }
          },
        },
      );
    },
    [effectiveId, otherLocaleLabel, apiCall, refreshData],
  );

  const handleSeoSuggestions = useCallback(() => {
    if (!effectiveId) {
      message.warning("Sauvegardez d'abord l'article");
      return;
    }
    setLoadingSeo(true);
    fetchSeo(
      { url: `/api/v1/admin/marketing/blog/${effectiveId}/seo-suggestions`, method: 'post', values: {} },
      {
        onSuccess: (res) => {
          const d = res?.data ?? {};
          setSuggestions({
            suggestedSeoTitle: d?.suggestedSeoTitle ?? d?.seoTitle ?? '',
            suggestedSeoDescription: d?.suggestedSeoDescription ?? d?.seoDescription ?? '',
            suggestedKeywords: d?.suggestedKeywords ?? d?.keywords ?? [],
          });
          setLoadingSeo(false);
        },
        onError: () => {
          message.error('Erreur SEO');
          setLoadingSeo(false);
        },
      },
    );
  }, [effectiveId, fetchSeo]);

  return {
    // State
    title,
    slug,
    seoTitle,
    seoDescription,
    seoKeywords,
    persona,
    customPersona,
    excerpt,
    featuredImageUrl,
    locale,
    autoTranslate,
    generationStep,
    loadingSeo,
    suggestions,
    detailLoading,
    translating,
    imagePreviewOpen,
    isGenerating,
    isEdit,
    effectiveId,
    otherLocaleLabel,
    saving,
    // Setters
    setTitle,
    setSlug,
    setSeoTitle,
    setSeoDescription,
    setSeoKeywords,
    setPersona,
    setCustomPersona,
    setExcerpt,
    setFeaturedImageUrl,
    setLocale,
    setAutoTranslate,
    setImagePreviewOpen,
    // Handlers
    handleSave,
    handleGenerateAI,
    handleTranslateOrSync,
    handleSeoSuggestions,
    refreshData,
  };
}
